/**
 * TRUS-719: values for a member-message step's `input` variables.
 *
 * A template variable of kind `input` (e.g. {{clinic_day}}) has no member or
 * org source: someone must type its value. In a manual send the sender does;
 * in a workflow nobody is there, so the builder supplies it here and it is
 * saved on the step as `config.variable_inputs: {key: value}`. The workflow
 * outbox hands those to the resolver (TRUS-705), so the step no longer
 * blocks on them.
 *
 * Which inputs a template needs comes from POST /api/template-variables/validate
 * (`required_inputs`), checked as the subject / body change. When that call
 * fails, or the template needs no inputs, nothing renders and the step saves
 * exactly as before.
 */
import { useEffect, useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { templateVariablesApi, type RequiredInput } from '@/components/messaging/templateVariables/api';
import { VariableInputFields } from '@/components/messaging/templateVariables/VariableInputFields';

export type VariableInputs = Record<string, string>;

/** Saved `config.variable_inputs`, cleaned to string values (anything else dropped). */
export function variableInputsOf(config: Record<string, unknown> | null | undefined): VariableInputs {
  const raw = config?.variable_inputs;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: VariableInputs = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
  }
  return out;
}

/**
 * The step config with `variable_inputs` set to `inputs`, minus blank values;
 * the key is removed entirely when nothing is left (the config then looks as
 * it did before this feature).
 */
export function withVariableInputs<C extends Record<string, unknown>>(config: C, inputs: VariableInputs): C {
  const cleaned: VariableInputs = {};
  for (const [k, v] of Object.entries(inputs)) {
    if (typeof v === 'string' && v.trim() !== '') cleaned[k] = v;
  }
  const next = { ...config } as Record<string, unknown>;
  if (Object.keys(cleaned).length) next.variable_inputs = cleaned;
  else delete next.variable_inputs;
  return next as C;
}

interface StepVariableInputsProps {
  subject: string | null;
  body: string;
  value: VariableInputs;
  onChange: (next: VariableInputs) => void;
  /** Validate debounce (ms); tests pass 0. */
  debounceMs?: number;
}

export function StepVariableInputs({ subject, body, value, onChange, debounceMs = 600 }: StepVariableInputsProps) {
  const { t } = useAppTranslation();
  const [inputs, setInputs] = useState<RequiredInput[]>([]);

  useEffect(() => {
    const text = `${subject ?? ''}\n${body ?? ''}`;
    if (!/\{[^{}]+\}/.test(text)) {
      setInputs([]);
      return;
    }
    let cancelled = false;
    const h = window.setTimeout(() => {
      templateVariablesApi
        .validate({ subject: subject ?? null, body: body ?? '', applies_to: 'messages' })
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res?.required_inputs) ? res.required_inputs : [];
          setInputs(list.filter((i) => i && typeof i.key === 'string' && i.key));
        })
        // Older backend / flaky check: today's behaviour, no input fields.
        .catch(() => !cancelled && setInputs([]));
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(h);
    };
  }, [subject, body, debounceMs]);

  if (inputs.length === 0) return null;

  return (
    <div className="space-y-1" data-testid="step-variable-inputs">
      <p className="text-xs text-gray-500">
        {t(
          'templateVariables.workflow.inputsHint',
          'These variables are normally filled in by whoever sends the message. A workflow sends it on its own, so enter the values here.',
        )}
      </p>
      <VariableInputFields inputs={inputs} values={value} onChange={onChange} idPrefix="step-var-input" />
    </div>
  );
}

export default StepVariableInputs;
